import { editUser } from '@api/users';
import { RolesTenantsMappings } from '@components/Authorization';
import { VerifiedIcon } from '@components/Icons';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	InputAdornment,
	Typography
} from '@mui/material';
import { getExternalIdsString } from '@utils/users';
import { emailValidationScheme } from '@utils/validation';
import { USERS_FIELDS } from 'constants/global';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import { compact } from 'lodash';
import React from 'react';
import { ITenant } from 'types/tenants';
import { ToastSeverity } from 'types/toast';
import { IUser } from 'types/users';
import { object } from 'yup';
import UserChip from '../Chip/UserChip';

interface IUserSingleEditDialogProps {
	user: IUser;
	onCancel: () => void;
	onSaved: () => void;
}

const UserSingleEditDialog: React.FC<IUserSingleEditDialogProps> = ({
	user,
	onCancel,
	onSaved
}) => {
	const { addToast } = useToast();

	const displayName =
		user.displayName || getExternalIdsString(user.externalIDs);

	const formik = useFormik({
		initialValues: {
			[USERS_FIELDS.id]: user.id || '',
			[USERS_FIELDS.externalIDs]: getExternalIdsString(user.externalIDs) || '',
			[USERS_FIELDS.displayName]: user.displayName || '',
			[USERS_FIELDS.email]: user.email || '',
			[USERS_FIELDS.phoneNumber]: user.phoneNumber || '',
			mappings: user.mappings || []
		},
		validationSchema: object({
			[USERS_FIELDS.email]: emailValidationScheme()
		}),
		onSubmit: async (values) => {
			try {
				await editUser({
					...values,
					tenants: compact(formik.values.tenants as unknown as ITenant[]).map(
						(t: ITenant) => t.id
					),
					id: user.id,
					version: user.version
				});
				addToast({
					severity: ToastSeverity.Success,
					content: intl.tr('users.single.edit.dialog.success', {
						name: displayName
					})
				});
			} catch (error) {
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr('users.single.edit.dialog.error', {
						name: displayName
					}),
					error
				});
			}

			onSaved();
		}
	});

	const fieldsPrefix = 'users.single.edit.input.';

	return (
		<Dialog open maxWidth="sm" data-testid="user-single-edit-dialog">
			<DialogTitle>
				<Box display="flex" alignItems="center" flexDirection="row">
					<Typography
						variant="h5"
						textOverflow="ellipsis"
						overflow="hidden"
						noWrap
						title={displayName}
					>
						{displayName}
					</Typography>
					<Box pl={1}>
						<UserChip status={user.status} />
					</Box>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Box pt={2} pb={2}>
					<Grid container spacing={2}>
						<Grid item container xs={12}>
							<TextFieldValidation
								id={USERS_FIELDS.id}
								formik={formik}
								required
								disabled
								fullWidth
								label={intl.tr(`${fieldsPrefix}${USERS_FIELDS.id}`)}
							/>
						</Grid>
						<Grid item container xs={12}>
							<TextFieldValidation
								id={USERS_FIELDS.externalIDs}
								formik={formik}
								required
								disabled
								fullWidth
								label={intl.tr(`${fieldsPrefix}${USERS_FIELDS.externalIDs}`)}
							/>
						</Grid>
						<Grid item container xs={12}>
							<TextFieldValidation
								id={USERS_FIELDS.displayName}
								formik={formik}
								fullWidth
								label={intl.tr(`${fieldsPrefix}${USERS_FIELDS.displayName}`)}
							/>
						</Grid>
						<Grid item container xs={12}>
							<TextFieldValidation
								id={USERS_FIELDS.email}
								formik={formik}
								fullWidth
								label={intl.tr(`${fieldsPrefix}${USERS_FIELDS.email}`)}
								InputProps={{
									endAdornment: formik.values.email &&
										!formik.touched.email && (
											<InputAdornment position="end">
												<VerifiedIcon
													isVerified={!!user.verifiedEmail}
													verifiedTitle={intl.tr('users.email.verified')}
													unverifiedTitle={intl.tr('users.email.unverified')}
												/>
											</InputAdornment>
										)
								}}
							/>
						</Grid>
						<Grid item container xs={12}>
							<TextFieldValidation
								id={USERS_FIELDS.phoneNumber}
								formik={formik}
								fullWidth
								label={intl.tr(`${fieldsPrefix}${USERS_FIELDS.phoneNumber}`)}
								InputProps={{
									endAdornment: formik.values.phoneNumber &&
										!formik.touched.phoneNumber && (
											<InputAdornment position="end">
												<VerifiedIcon
													isVerified={!!user.verifiedPhone}
													verifiedTitle={intl.tr('users.phoneNumber.verified')}
													unverifiedTitle={intl.tr(
														'users.phoneNumber.unverified'
													)}
												/>
											</InputAdornment>
										)
								}}
							/>
						</Grid>
						<Grid item container xs={12}>
							<Box flexDirection="column" flexGrow={1}>
								<Typography pl={1} py={1}>
									{intl.tr('dialog.authorization.header')}
								</Typography>
								<RolesTenantsMappings formik={formik} id="mappings" />
							</Box>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions sx={{ paddingRight: 3, paddingBottom: 3 }}>
				<Button onClick={onCancel} data-testid="user-single-edit-cancel-button">
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					disabled={!formik.isValid || !formik.dirty}
					loading={formik.isSubmitting}
					onClick={() => formik.handleSubmit()}
					data-testid="user-single-edit-submit-button"
				>
					{intl.tr('button.save')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default UserSingleEditDialog;
